import React, { useState } from 'react';
import {
  Modal,
  ModalVariants,
  Typography,
  Button,
} from '@hid-galaxy-ui/galaxy-react';
import Iconography, {
  IconographySizes,
} from '@hid-galaxy-ui/galaxy-react/components/Iconography';
import { IconographyThemes } from '@hid-galaxy-ui/galaxy-react/components/Iconography/iconEnums';
import { TypographyVariantEnum } from '@hid-galaxy-ui/galaxy-react/components/Typography/typographyEnum';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import {
  IPhotoValidationResult,
  IPhotoValidationRule,
} from '../../constants/photoValidation';
import {
  extractHexColor,
  getFailedRules,
  getFriendlyColorName,
  getPassedRules,
  getPhotoValidationProgress,
  getRuleDisplayName,
  getRuleHowToFix,
  isBackgroundColorRule,
  isWarnOnlyWithIssues,
} from '../../utils/photoValidation';
import { getRuleIcon } from './ruleIcons';
import { usePhotoValidationModalStyles } from './photoValidationModalStyles';

export interface IPhotoValidationModalProps {
  open: boolean;
  result: IPhotoValidationResult | null;
  previewUrl?: string | null;
  onReplacePhoto: () => void;
  onClose: () => void;
}

interface IPassedRuleRowProps {
  rule: IPhotoValidationRule;
}

const PassedRuleRow: React.FC<IPassedRuleRowProps> = ({ rule }) => {
  const classes = usePhotoValidationModalStyles();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const hasMessage = !!rule.message;

  return (
    <div className={classes.passedRow}>
      <button
        type="button"
        className={classes.passedRowHeader}
        onClick={() => hasMessage && setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        <Iconography
          icon="circleCheck"
          size={IconographySizes.Medium}
          theme={IconographyThemes.Success}
          className={classes.passedIcon}
        />
        <span className={classes.passedName}>
          {getRuleDisplayName(rule.ruleName, t)}
        </span>
        {hasMessage && (
          <Iconography
            icon="circleChevronDown"
            size={IconographySizes.Small}
            className={clsx(classes.chevron, expanded && classes.chevronOpen)}
          />
        )}
      </button>
      {hasMessage && expanded && (
        <Typography
          variant={TypographyVariantEnum.TextHelper}
          className={classes.passedMessage}
        >
          {rule.message}
        </Typography>
      )}
    </div>
  );
};

interface IFailedRuleCardProps {
  rule: IPhotoValidationRule;
  isWarning?: boolean;
}

const FailedRuleCard: React.FC<IFailedRuleCardProps> = ({
  rule,
  isWarning = false,
}) => {
  const classes = usePhotoValidationModalStyles();
  const { t } = useTranslation();
  const howToFix = getRuleHowToFix(rule.ruleName, t);

  const requiredBackgroundHex = isBackgroundColorRule(rule.ruleName)
    ? extractHexColor(rule.message)
    : null;
  const backgroundColorName = requiredBackgroundHex
    ? getFriendlyColorName(requiredBackgroundHex, t)
    : null;

  return (
    <div
      className={clsx(classes.failedCard, isWarning && classes.warningCard)}
    >
      <span
        className={clsx(
          classes.failedBadge,
          isWarning && classes.warningBadge,
        )}
      >
        <Iconography
          icon={isWarning ? 'triangleExclamation' : 'xmark'}
          size={IconographySizes.Small}
          className={classes.failedBadgeIcon}
        />
      </span>
      <div className={classes.failedContent}>
        <Typography
          variant={TypographyVariantEnum.Label}
          className={classes.failedTitle}
        >
          {getRuleDisplayName(rule.ruleName, t)}
        </Typography>
        {requiredBackgroundHex ? (
          <div className={classes.colorSwatchRow}>
            <span className={classes.colorSwatchLabel}>
              {t('PHOTO_VALIDATION_REQUIRED_BACKGROUND') as string}
            </span>
            <span
              className={classes.colorSwatch}
              style={{ backgroundColor: requiredBackgroundHex }}
              aria-hidden="true"
            />
            {backgroundColorName && (
              <span className={classes.colorSwatchName}>
                {backgroundColorName}
              </span>
            )}
          </div>
        ) : (
          rule.message && (
            <Typography
              variant={TypographyVariantEnum.TextHelper}
              className={classes.failedMessage}
            >
              {rule.message}
            </Typography>
          )
        )}
        {howToFix && (
          <div
            className={clsx(
              classes.howToFix,
              isWarning && classes.warningHowToFix,
            )}
          >
            <Typography
              variant={TypographyVariantEnum.TextSmall}
              className={clsx(
                classes.howToFixLabel,
                isWarning && classes.warningHowToFixLabel,
              )}
            >
              {t('PHOTO_VALIDATION_HOW_TO_FIX') as string}
            </Typography>
            <Typography
              variant={TypographyVariantEnum.TextHelper}
              className={classes.howToFixText}
            >
              {howToFix}
            </Typography>
          </div>
        )}
      </div>
      <span
        className={clsx(
          classes.failedIllustration,
          isWarning && classes.warningIllustration,
        )}
      >
        {getRuleIcon(rule.ruleName, {
          className: classes.failedIllustrationIcon,
        })}
      </span>
    </div>
  );
};

const PhotoValidationModal: React.FC<IPhotoValidationModalProps> = ({
  open,
  result,
  previewUrl,
  onReplacePhoto,
  onClose,
}) => {
  const classes = usePhotoValidationModalStyles();
  const { t } = useTranslation();

  if (!open || !result) {
    return null;
  }

  const isWarnOnly = isWarnOnlyWithIssues(result);
  const passedRules = getPassedRules(result);
  const failedRules = getFailedRules(result);
  const { failed, total } = getPhotoValidationProgress(result);

  return (
    <Modal
      size={ModalVariants.Large}
      isDisplayCloseIcon
      onClose={onClose}
      className={classes.modal}
      isFocusTrapActive={false}
    >
      <div className={classes.root}>
        <div className={classes.header}>
          <Iconography
            icon={isWarnOnly ? 'triangleExclamation' : 'circleExclamation'}
            size={IconographySizes.XLarge}
            theme={
              isWarnOnly
                ? IconographyThemes.Warning
                : IconographyThemes.Error
            }
            className={clsx(
              classes.headerIcon,
              isWarnOnly && classes.headerIconWarning,
            )}
          />
          <div className={classes.headerText}>
            <Typography
              id="photo-validation-title"
              variant={TypographyVariantEnum.H2}
              className={classes.title}
            >
              {
                t(
                  isWarnOnly
                    ? 'PHOTO_VALIDATION_WARN_TITLE'
                    : 'PHOTO_VALIDATION_TITLE',
                ) as string
              }
            </Typography>
            <Typography
              variant={TypographyVariantEnum.TextHelper}
              className={classes.subtitle}
            >
              {
                t(
                  isWarnOnly
                    ? 'PHOTO_VALIDATION_WARN_SUBTITLE'
                    : 'PHOTO_VALIDATION_SUBTITLE',
                ) as string
              }
            </Typography>
          </div>
          <div
            className={clsx(
              classes.headerBadge,
              isWarnOnly && classes.headerBadgeWarning,
            )}
          >
            <span
              className={clsx(
                classes.headerBadgeCount,
                isWarnOnly && classes.headerBadgeCountWarning,
              )}
            >
              {failed}/{total}
            </span>
            <span
              className={clsx(
                classes.headerBadgeLabel,
                isWarnOnly && classes.headerBadgeLabelWarning,
              )}
            >
              {
                t(
                  isWarnOnly
                    ? 'PHOTO_VALIDATION_WARNINGS'
                    : 'PHOTO_VALIDATION_FAILED',
                ) as string
              }
            </span>
          </div>
        </div>

        <hr className={classes.divider} />

        <div className={classes.body}>
          <div className={classes.leftPanel}>
            <Typography
              variant={TypographyVariantEnum.Label}
              className={classes.panelHeading}
            >
              {t('PHOTO_VALIDATION_PHOTO_PREVIEW') as string}
            </Typography>
            <div className={classes.previewImageWrap}>
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt={t('PHOTO_VALIDATION_PHOTO_PREVIEW') as string}
                  className={classes.previewImage}
                />
              ) : (
                <span className={classes.previewPlaceholder}>
                  {t('PHOTO_VALIDATION_PHOTO_PREVIEW') as string}
                </span>
              )}
            </div>
            {isWarnOnly ? (
              <div className={clsx(classes.infoBox, classes.uploadedInfoBox)}>
                <Iconography
                  icon="circleCheck"
                  size={IconographySizes.Small}
                  theme={IconographyThemes.Success}
                  className={classes.uploadedInfoIcon}
                />
                <div>
                  <Typography
                    variant={TypographyVariantEnum.Label}
                    className={classes.infoTitle}
                  >
                    {t('PHOTO_VALIDATION_UPLOADED_TITLE') as string}
                  </Typography>
                  <Typography
                    variant={TypographyVariantEnum.TextHelper}
                    className={classes.uploadedInfoText}
                  >
                    {t('PHOTO_VALIDATION_UPLOADED_MESSAGE') as string}
                  </Typography>
                </div>
              </div>
            ) : (
              <div className={classes.infoBox}>
                <Iconography
                  icon="circleInfo"
                  size={IconographySizes.Small}
                  theme={IconographyThemes.Info}
                  className={classes.infoIcon}
                />
                <div>
                  <Typography
                    variant={TypographyVariantEnum.Label}
                    className={classes.infoTitle}
                  >
                    {t('PHOTO_VALIDATION_BEST_RESULTS_TITLE') as string}
                  </Typography>
                  <ul className={classes.infoList}>
                    <li>
                      {t('PHOTO_VALIDATION_BEST_RESULTS_LIGHTING') as string}
                    </li>
                    <li>
                      {t('PHOTO_VALIDATION_BEST_RESULTS_CAMERA') as string}
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          <div className={classes.rightPanel}>
            {failedRules.length > 0 && (
              <>
                <Typography
                  variant={TypographyVariantEnum.Label}
                  className={clsx(
                    classes.sectionHeading,
                    isWarnOnly
                      ? classes.sectionHeadingWarning
                      : classes.sectionHeadingFailed,
                  )}
                >
                  {
                    t(
                      isWarnOnly
                        ? 'PHOTO_VALIDATION_WARNING_CHECKS'
                        : 'PHOTO_VALIDATION_FAILED_CHECKS',
                    ) as string
                  }
                </Typography>
                <div className={classes.failedList}>
                  {failedRules.map((rule) => (
                    <FailedRuleCard
                      key={rule.ruleName}
                      rule={rule}
                      isWarning={isWarnOnly}
                    />
                  ))}
                </div>
              </>
            )}

            {passedRules.length > 0 && (
              <>
                <Typography
                  variant={TypographyVariantEnum.Label}
                  className={clsx(
                    classes.sectionHeading,
                    classes.sectionHeadingPassed,
                    failedRules.length > 0 && classes.sectionSpacing,
                  )}
                >
                  {t('PHOTO_VALIDATION_PASSED_CHECKS') as string}
                </Typography>
                <div className={classes.passedList}>
                  {passedRules.map((rule) => (
                    <PassedRuleRow key={rule.ruleName} rule={rule} />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className={classes.footer}>
        <Button
          variant="secondary"
          className={classes.replaceButton}
          icon={<Iconography icon="upload" size={IconographySizes.Small} />}
          label={t('PHOTO_VALIDATION_REPLACE_PHOTO') as string}
          onClick={onReplacePhoto}
        />
        <Button
          variant="primary"
          className={classes.okButton}
          label={
            t(
              isWarnOnly
                ? 'PHOTO_VALIDATION_KEEP_PHOTO'
                : 'PHOTO_VALIDATION_OK',
            ) as string
          }
          onClick={onClose}
        />
      </div>
    </Modal>
  );
};

export default PhotoValidationModal;
